import type { ProfileData } from './Profile';

/**
 * Inline milestone unlocks (proto-achievements; the full achievements system
 * can formalize these later without rework). Everything here is smoothing;
 * capped POWER progression lives in the purchased upgrade tracks
 * (metaUpgrades.ts — revised pillar, July 2026). Characters are sideways
 * choices, not power tiers; the first-win reward is guidedPicks (early-offer
 * forgiveness runs one pick longer — the catch-up knob, not a reroll).
 */

export type MilestoneId =
  | 'firstWin' | 'collector4' | 'deepWin' | 'survivor3'
  | 'charVane' | 'charThirl' | 'charCask' | 'charTrine'
  | 'charMourne' | 'charBramGaunt';

export interface Milestone {
  id: MilestoneId;
  unlock: keyof ProfileData['unlocks'];
  label: string;
  test: (p: ProfileData, run: { win: boolean; floor: number }) => boolean;
}

export const MILESTONES: Milestone[] = [
  {
    id: 'firstWin',
    unlock: 'guidedPicks',
    label: 'Guided Hand — early picks stay forgiving longer',
    test: (p) => p.stats.wins >= 1,
  },
  {
    id: 'collector4',
    unlock: 'artifactSlot1',
    label: 'Artifact Slot +1 — carry 6',
    test: (p) => Object.keys(p.collection).length >= 4,
  },
  {
    id: 'deepWin',
    unlock: 'artifactSlot2',
    label: 'Artifact Slot +2 — carry 7',
    test: (_, run) => run.win && run.floor >= 3,
  },
  {
    id: 'survivor3',
    unlock: 'startingMend',
    label: "Traveler's Pocket — start with a Mend",
    test: (p) => p.stats.bestFloor >= 3,
  },
  // Character gates: each aims at the flavor of the character it opens.
  {
    id: 'charVane',
    unlock: 'charVane',
    label: 'Vane, the Knifedancer — new diver',
    test: (p) => p.stats.bestFloor >= 3,
  },
  {
    id: 'charThirl',
    unlock: 'charThirl',
    label: 'Thirl, the Bloodbound — new diver',
    test: (p) => p.stats.deaths >= 10,
  },
  {
    id: 'charCask',
    unlock: 'charCask',
    label: 'Cask, the Powdermonk — new diver',
    test: (p) => p.stats.wins >= 1,
  },
  {
    id: 'charTrine',
    unlock: 'charTrine',
    label: 'Trine, the Prism-conjurer — new diver',
    test: (p) => p.stats.wins >= 3,
  },
  {
    id: 'charMourne',
    unlock: 'charMourne',
    label: 'Mourne, the Bonewright — new diver',
    test: (p) => p.stats.totalKills >= 3000,
  },
  {
    id: 'charBramGaunt',
    unlock: 'charBramGaunt',
    label: 'Bram & Gaunt, the Houndkeeper — new divers',
    test: (p) => Object.keys(p.collection).length >= 8,
  },
];

/**
 * Evaluate after banking/stats. Idempotent: already-unlocked milestones never
 * re-fire. Returns the newly unlocked milestones (caller saves).
 */
export function evaluateMilestones(
  data: ProfileData,
  run: { win: boolean; floor: number },
): Milestone[] {
  const fired: Milestone[] = [];
  for (const m of MILESTONES) {
    if (data.unlocks[m.unlock]) continue;
    if (m.test(data, run)) {
      data.unlocks[m.unlock] = true;
      fired.push(m);
    }
  }
  return fired;
}
